import {

  Box, Chip, FormControlLabel, Grid, Link, MenuItem, Stack, Switch, TextField, Typography, Alert,

} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import ZonePolygonMap from '../../../components/maps/ZonePolygonMap';
import MapErrorBoundary from '../../../components/maps/MapErrorBoundary';
import GeoMasterSelect from '../../../components/masters/GeoMasterSelect';
import NonNegativeNumberField from '../../../components/ui/forms/NonNegativeNumberField';

import { getMasters } from '../../../services/platform/masterService';
import {
  PAYOUT_MODELS,
  ZONE_COMMISSION_RATE_DECIMALS,
  ZONE_COMMISSION_RATE_MAX,
  ZONE_COMMISSION_RATE_MIN,
  commissionRateUsesZonePercent,
} from '../../../constants/transportPartnerFlow';
import { defaultsForCountrySelection, currencyOptionsForCountry, timezoneOptionsForCountry } from '../../../constants/countryDefaults';

import api from '../../../services/api';
import type { HubServiceOption, VehicleTypeOption, ZoneFormState, ZoneStep0FieldErrors } from '../zoneTypes';

import {
  applyDefaultZoneCatalog, buildServicePricingRow, buildVehiclePricingRow, FALLBACK_VEHICLES, filterFormServicesToPool, isPorterService, isRideHailingService, sanitizeZoneName, formatZoneDisplayName, serviceSlugForVehicle, validateZoneName, isDuplicateZoneName, validateZoneCode, isDuplicateZoneCode, validateZoneCommissionRate, syncZoneFormWithSelectedServices,
} from '../zoneTypes';

import type { ServiceHubType } from '../../../constants/serviceHubs';

const PAYMENT_OPTIONS = ['cash', 'wallet', 'card', 'upi'];

const HUB_LABELS: Record<string, string> = {
  transport: 'Transport', delivery: 'Delivery', on_demand: 'On Demand', video_consultation: 'Video', booking: 'Booking',
};

interface Props {
  form: ZoneFormState;
  setForm: (f: ZoneFormState | ((prev: ZoneFormState) => ZoneFormState)) => void;
  allServices: HubServiceOption[];
  primaryZones: Array<{
    _id: string;
    code: string;
    name: string;
    serviceIds?: Array<{ _id: string } | string>;
    paymentTypes?: string[];
  }>;
  showErrors?: boolean;
  fieldErrors?: ZoneStep0FieldErrors;
  existingZones?: Array<{ _id: string; name: string; code: string }>;
  excludeZoneId?: string;
  /** When set (e.g. from Food Delivery hub), only list that hub's services. */
  scopeHub?: ServiceHubType;
  /** Prefer / lock this service slug (food-delivery / grocery-delivery). */
  lockedServiceSlug?: string;
}

export default function ZoneLocationStep({
  form, setForm, allServices, primaryZones, showErrors, fieldErrors, existingZones = [], excludeZoneId,
  scopeHub, lockedServiceSlug,
}: Props) {

  const nameFieldError = useMemo(() => {
    const fromParent = fieldErrors?.name;
    if (fromParent) return fromParent;
    const formatError = validateZoneName(form.name);
    if (formatError && (showErrors || form.name.length > 0)) return formatError;
    if (
      form.name.trim()
      && isDuplicateZoneName(form.name, existingZones, excludeZoneId)
    ) {
      return 'Zone name already exists.';
    }
    return undefined;
  }, [fieldErrors?.name, form.name, existingZones, excludeZoneId, showErrors]);

  const codeFieldError = useMemo(() => {
    const fromParent = fieldErrors?.code;
    if (fromParent) return fromParent;
    const formatError = validateZoneCode(form.code);
    if (formatError && (showErrors || form.code.length > 0)) return formatError;
    if (
      form.code.trim()
      && isDuplicateZoneCode(form.code, existingZones, excludeZoneId)
    ) {
      return 'Zone code already exists.';
    }
    return undefined;
  }, [fieldErrors?.code, form.code, existingZones, excludeZoneId, showErrors]);

  const commissionFieldError = useMemo(() => {
    if (!commissionRateUsesZonePercent(form.partnerPayoutModel)) return undefined;
    const fromParent = fieldErrors?.commissionRate;
    if (fromParent) return fromParent;
    return validateZoneCommissionRate(form.commissionRate, form.partnerPayoutModel);
  }, [fieldErrors?.commissionRate, form.commissionRate, form.partnerPayoutModel]);

  // Secondary zone payment methods must match the selected primary zone.
  const allowedPaymentTypes = useMemo(() => {
    if (form.zoneLevel !== 'SECONDARY' || !form.primaryZoneId) return PAYMENT_OPTIONS;
    const primary = primaryZones.find((z) => z._id === form.primaryZoneId);
    const allowed = primary?.paymentTypes?.length ? primary.paymentTypes : [];
    return allowed.length ? allowed : PAYMENT_OPTIONS;
  }, [form.zoneLevel, form.primaryZoneId, primaryZones]);

  useEffect(() => {
    if (form.zoneLevel !== 'SECONDARY' || !form.primaryZoneId) return;
    setForm((prev) => {
      const filtered = prev.paymentTypes.filter((p) => allowedPaymentTypes.includes(p));
      // If nothing matches (e.g. defaults from empty form), default to all allowed payment types from primary.
      const paymentTypes = filtered.length ? filtered : allowedPaymentTypes;
      const defaultPaymentType = paymentTypes.includes(prev.defaultPaymentType)
        ? prev.defaultPaymentType
        : (paymentTypes[0] || 'cash');
      return { ...prev, paymentTypes, defaultPaymentType };
    });
  }, [form.zoneLevel, form.primaryZoneId, allowedPaymentTypes, setForm]);

  const { data: vehicleMaster, isFetched: vehiclesFetched } = useQuery({
    queryKey: ['vehicle-types-master'],
    queryFn: async () => {
      const res = await getMasters('vehicle_types', { limit: 50 });
      const rows = (res.data || []) as Array<{ code: string; name: string }>;
      return rows.length ? rows.map((r) => ({ code: r.code, name: r.name })) : FALLBACK_VEHICLES;
    },
    enabled: !(scopeHub === 'delivery' || scopeHub === 'on_demand' || !!lockedServiceSlug),
  });

  const vehicleTypes = vehicleMaster || FALLBACK_VEHICLES;

  const hasTransportService = useMemo(
    () => form.selectedServiceIds.some((id) => {
      const svc = allServices.find((s) => s._id === id);
      return svc && isRideHailingService(svc.slug);
    }),
    [form.selectedServiceIds, allServices],
  );

  const servicePool = useMemo(() => {
    let pool = allServices;
    if (scopeHub) {
      pool = pool.filter((s) => s.hubType === scopeHub);
    }
    if (lockedServiceSlug) {
      const locked = pool.filter((s) => s.slug === lockedServiceSlug);
      if (locked.length) pool = locked;
    }
    if (form.zoneLevel === 'SECONDARY' && form.primaryZoneId) {
      const primary = primaryZones.find((z) => z._id === form.primaryZoneId);
      const ids = (primary?.serviceIds || []).map((s) => (typeof s === 'string' ? s : s._id));
      if (ids.length) {
        const inherited = pool.filter((s) => ids.includes(s._id));
        // When delivery/food scoped, keep the locked delivery service even if primary
        // only had transport services historically.
        if (inherited.length) return inherited;
        if (scopeHub === 'delivery' || scopeHub === 'on_demand' || lockedServiceSlug) return pool;
      }
    }
    return pool;
  }, [allServices, form.zoneLevel, form.primaryZoneId, primaryZones, scopeHub, lockedServiceSlug]);

  const onDemandScoped = scopeHub === 'on_demand';
  const hideTransportUi = scopeHub === 'delivery' || onDemandScoped || !!lockedServiceSlug;
  const hasRideHailing = form.selectedServiceIds.some((id) => {

    const svc = allServices.find((s) => s._id === id);

    return svc && isRideHailingService(svc.slug);

  });

  const hasPorter = form.selectedServiceIds.some((id) => {
    const svc = allServices.find((s) => s._id === id);
    return svc && isPorterService(svc.slug);
  });

  const catalogHasPorter = servicePool.some((s) => isPorterService(s.slug));

  useEffect(() => {
    setForm((prev) => filterFormServicesToPool(prev, servicePool, allServices));
  }, [form.zoneLevel, form.primaryZoneId, servicePool, allServices, setForm]);

  const { data: porterMasters } = useQuery({
    queryKey: ['porter-zone-masters'],
    queryFn: async () => {
      const [vehicles, categories, packages] = await Promise.all([
        api.get('/porter/vehicles').then((r) => r.data?.data || []),
        api.get('/porter/categories').then((r) => r.data?.data || []),
        api.get('/porter/packages').then((r) => r.data?.data || []),
      ]);
      return {
        vehicles: vehicles as Array<{ _id: string; name: string }>,
        categories: categories as Array<{ _id: string; name: string }>,
        packages: packages as Array<{ _id: string; name: string }>,
      };
    },
    enabled: catalogHasPorter,
  });

  useEffect(() => {
    if (hideTransportUi) {
      setForm((prev) => applyDefaultZoneCatalog(prev, servicePool, [], porterMasters || null));
      return;
    }
    if (!servicePool.length || !vehiclesFetched) return;
    setForm((prev) => applyDefaultZoneCatalog(prev, servicePool, vehicleTypes, porterMasters || null));
  }, [servicePool, vehicleTypes, porterMasters, vehiclesFetched, setForm, hideTransportUi]);

  const togglePorterMaster = (field: 'vehicleIds' | 'categoryIds' | 'packageIds', id: string) => {
    const current = form.porterMasters[field];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    setForm({ ...form, porterMasters: { ...form.porterMasters, [field]: next } });
  };

  const toggleService = (svc: HubServiceOption) => {

    const selected = form.selectedServiceIds.includes(svc._id);

    // Food/Grocery scoped create: keep the locked delivery service selected.
    if (selected && lockedServiceSlug && svc.slug === lockedServiceSlug) {
      return;
    }

    const nextIds = selected

      ? form.selectedServiceIds.filter((id) => id !== svc._id)

      : [...form.selectedServiceIds, svc._id];

    let nextPricing = selected

      ? form.servicePricing.filter((p) => p.serviceId !== svc._id)

      : [...form.servicePricing, buildServicePricingRow(svc)];

    if (!selected && isRideHailingService(svc.slug)) {

      nextPricing = nextPricing.filter((p) => p.serviceId !== svc._id);

    }

    const nextPorterMasters = selected && isPorterService(svc.slug)
      ? { vehicleIds: [], categoryIds: [], packageIds: [] }
      : !selected && isPorterService(svc.slug) && porterMasters
        ? {
          vehicleIds: porterMasters.vehicles.map((v) => v._id),
          categoryIds: porterMasters.categories.map((c) => c._id),
          packageIds: porterMasters.packages.map((p) => p._id),
        }
        : form.porterMasters;

    let nextVehicleCodes = form.selectedVehicleTypeCodes;
    let nextVehiclePricing = form.vehiclePricing;

    setForm(syncZoneFormWithSelectedServices({
      ...form,
      selectedServiceIds: nextIds,
      servicePricing: nextPricing,
      porterMasters: nextPorterMasters,
      selectedVehicleTypeCodes: nextVehicleCodes,
      vehiclePricing: nextVehiclePricing,
    }, allServices));

  };



  const toggleVehicle = (vehicle: VehicleTypeOption) => {

    const selected = form.selectedVehicleTypeCodes.includes(vehicle.code);

    const slug = serviceSlugForVehicle(vehicle.code);

    const linked = allServices.find((s) => s.slug === slug);

    const nextCodes = selected

      ? form.selectedVehicleTypeCodes.filter((c) => c !== vehicle.code)

      : [...form.selectedVehicleTypeCodes, vehicle.code];

    let nextVehiclePricing = selected

      ? form.vehiclePricing.filter((v) => v.vehicleTypeCode !== vehicle.code)

      : [...form.vehiclePricing];

    if (!selected) {

      const row = buildVehiclePricingRow(vehicle, allServices);

      if (row) nextVehiclePricing = [...nextVehiclePricing, row];

    }

    let nextServiceIds = [...form.selectedServiceIds];

    if (linked && !nextServiceIds.includes(linked._id)) {

      nextServiceIds = [...nextServiceIds, linked._id];

    }

    if (selected && linked) {

      const stillNeeded = nextCodes.some((c) => serviceSlugForVehicle(c) === linked.slug);

      if (!stillNeeded) nextServiceIds = nextServiceIds.filter((id) => id !== linked._id);

    }

    setForm(syncZoneFormWithSelectedServices({
      ...form,
      selectedVehicleTypeCodes: nextCodes,
      vehiclePricing: nextVehiclePricing,
      selectedServiceIds: nextServiceIds,
    }, allServices));

  };



  const grouped = servicePool.reduce<Record<string, HubServiceOption[]>>((acc, s) => {

      (acc[s.hubType] ||= []).push(s);

      return acc;

    }, {});



  return (

    <Stack spacing={2}>

      <Typography variant="subtitle1" fontWeight={700}>Service location & zone boundary</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Zone name"
            value={form.name}
            required
            error={Boolean(nameFieldError)}
            helperText={nameFieldError || 'Letters only — use consistent Title Case (e.g. World Zone)'}
            onChange={(e) => {
              const name = sanitizeZoneName(e.target.value);
              setForm({ ...form, name });
            }}
            onBlur={() => {
              const titled = formatZoneDisplayName(form.name);
              if (titled !== '—' && titled !== form.name) {
                setForm({ ...form, name: titled });
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Zone code"
            value={form.code}
            required
            error={Boolean(codeFieldError)}
            helperText={codeFieldError || 'Required — unique code, 2–32 characters'}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <GeoMasterSelect
            label="Country"
            masterType="countries"
            value={form.country}
            onChange={(code, opt) => {
              const d = defaultsForCountrySelection(code, opt);
              setForm({ ...form, country: code, timezone: d.timezone, currency: d.currency });
            }}
            required
            error={Boolean(showErrors && fieldErrors?.country)}
            helperText={showErrors && fieldErrors?.country ? fieldErrors.country : 'Required'}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Currency"
            value={form.currency}
            disabled={!form.country}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          >
            {currencyOptionsForCountry(form.country, form.currency).map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Timezone"
            value={form.timezone}
            disabled={!form.country}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          >
            {timezoneOptionsForCountry(form.country, form.timezone).map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6} sm={3}>

          <TextField select fullWidth label="Zone level" value={form.zoneLevel} onChange={(e) => {
            const zoneLevel = e.target.value as 'PRIMARY' | 'SECONDARY';
            const next = zoneLevel === 'PRIMARY'
              ? { ...form, zoneLevel, primaryZoneId: '' }
              : { ...form, zoneLevel };
            setForm(filterFormServicesToPool(next, servicePool, allServices));
          }}>

            <MenuItem value="PRIMARY">Primary</MenuItem>

            <MenuItem value="SECONDARY">Secondary</MenuItem>

          </TextField>

        </Grid>

        {form.zoneLevel === 'SECONDARY' && (

          <Grid item xs={6} sm={3}>

            <TextField select fullWidth label="Primary zone" value={form.primaryZoneId} onChange={(e) => {
              const primaryZoneId = e.target.value;
              const primary = primaryZones.find((z) => z._id === primaryZoneId);
              const poolIds = new Set((primary?.serviceIds || []).map((s) => (typeof s === 'string' ? s : s._id)));
              const pool = poolIds.size
                ? allServices.filter((s) => poolIds.has(s._id))
                : allServices;
              setForm(filterFormServicesToPool({ ...form, primaryZoneId }, pool, allServices));
            }} required error={Boolean(showErrors && fieldErrors?.primaryZoneId)} helperText={showErrors ? fieldErrors?.primaryZoneId : undefined}>

              {primaryZones.map((z) => (
                <MenuItem key={z._id} value={z._id}>{formatZoneDisplayName(z.name)}</MenuItem>
              ))}

            </TextField>

          </Grid>

        )}

        <Grid item xs={6} sm={3}>

          <TextField select fullWidth label="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value as 'KM' | 'MILE' })}>

            <MenuItem value="KM">KM</MenuItem>

            <MenuItem value="MILE">MILE</MenuItem>

          </TextField>

        </Grid>

        <Grid item xs={12} sm={6}>

          <TextField fullWidth label="Map color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />

        </Grid>

        <Grid item xs={12}>

          <Typography variant="caption" color={showErrors && fieldErrors?.paymentTypes ? 'error' : 'text.secondary'}>
            Payment types{showErrors && fieldErrors?.paymentTypes ? ` — ${fieldErrors.paymentTypes}` : ''}
          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>

            {allowedPaymentTypes.map((p) => (

              <Chip

                key={p}

                label={p}

                color={form.paymentTypes.includes(p) ? 'primary' : 'default'}

                variant={form.paymentTypes.includes(p) ? 'filled' : 'outlined'}

                onClick={() => {

                  const next = form.paymentTypes.includes(p)

                    ? form.paymentTypes.filter((x) => x !== p)

                    : [...form.paymentTypes, p];

                  const defaultPaymentType = next.includes(form.defaultPaymentType)
                    ? form.defaultPaymentType
                    : (next[0] || form.defaultPaymentType);
                  setForm({ ...form, paymentTypes: next, defaultPaymentType });

                }}

              />

            ))}

          </Stack>

        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Default payment type"
            value={form.paymentTypes.includes(form.defaultPaymentType) ? form.defaultPaymentType : ''}
            onChange={(e) => setForm({ ...form, defaultPaymentType: e.target.value })}
            disabled={!form.paymentTypes.length}
            error={Boolean(showErrors && fieldErrors?.defaultPaymentType)}
            helperText={showErrors && fieldErrors?.defaultPaymentType
              ? fieldErrors.defaultPaymentType
              : 'Customer app selects this when booking a trip or requesting a service in this zone.'}
          >
            {form.paymentTypes.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {!hideTransportUi && (
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.allowVehicleTypeFallback}
                  onChange={(e) => setForm({ ...form, allowVehicleTypeFallback: e.target.checked })}
                />
              )}
              label="Allow vehicle type fallback"
            />
            <Typography variant="body2" fontWeight={600} color="text.primary" display="block" sx={{ ml: 6, mt: -0.5, opacity: 0.85 }}>
              Off by default. When on, dispatch may offer a trip to drivers of another enabled vehicle type if none of the requested type are nearby.
            </Typography>
          </Grid>
        )}

        {!hideTransportUi && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color={showErrors && fieldErrors?.searchRadius ? 'error' : undefined}
            >
              Driver search radius (km)
            </Typography>
            <Typography
              variant="caption"
              color={showErrors && fieldErrors?.searchRadius ? 'error' : 'text.secondary'}
              display="block"
              sx={{ mb: 1 }}
            >
              {showErrors && fieldErrors?.searchRadius
                ? fieldErrors.searchRadius
                : 'Zone overrides for transport dispatch. Search starts at the start radius and widens by the step until the max. Leave blank to use the service setting.'}
            </Typography>
            <Grid container spacing={2}>
              {([
                ['searchRadiusStartKm', 'Start radius'],
                ['searchRadiusStepKm', 'Step'],
                ['dispatchSearchRadiusKm', 'Dispatch max radius'],
                ['biddingSearchRadiusKm', 'Bidding max radius'],
              ] as const).map(([key, label]) => (
                <Grid item xs={6} sm={3} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={label}
                    value={form.searchRadius[key]}
                    onChange={(e) => setForm({
                      ...form,
                      searchRadius: { ...form.searchRadius, [key]: e.target.value },
                    })}
                    inputProps={{ min: 0, step: 0.5 }}
                    placeholder="Default"
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          {!hideTransportUi && (
            <>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.biddingEnabled}
                    onChange={(e) => setForm({ ...form, biddingEnabled: e.target.checked })}
                  />
                )}
                label="Bidding zone (transport / taxi)"
              />
              <Typography variant="body2" fontWeight={600} color="text.primary" display="block" sx={{ ml: 6, mt: -0.5, opacity: 0.85, lineHeight: 1.45 }}>
                When enabled, ride-now transport trips with pickup inside this zone&apos;s boundary use driver bidding
                for the whole zone — mobile apps read this via zone lookup and bidding APIs.
              </Typography>
              {form.biddingEnabled && !hasTransportService && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Select the Transport service for this zone — bidding applies to taxi/transport rides only.
                </Alert>
              )}
            </>
          )}
        </Grid>

        {!hideTransportUi && form.biddingEnabled && (
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.allowDriverCounterOffer}
                  onChange={(e) => setForm({ ...form, allowDriverCounterOffer: e.target.checked })}
                />
              )}
              label="Allow driver counter-offers"
            />
            <Typography variant="body2" fontWeight={600} color="text.primary" display="block" sx={{ ml: 6, mt: -0.5, opacity: 0.85 }}>
              When enabled, drivers can propose a higher fare after submitting their initial bid.
            </Typography>
          </Grid>
        )}

        {!hideTransportUi && form.biddingEnabled && (
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.autoAcceptLowestBid}
                  onChange={(e) => setForm({ ...form, autoAcceptLowestBid: e.target.checked })}
                />
              )}
              label="Auto-accept lowest bid when window expires"
            />
            <Typography variant="body2" fontWeight={600} color="text.primary" display="block" sx={{ ml: 6, mt: -0.5, opacity: 0.85 }}>
              After the bidding timeout, the cheapest eligible offer is assigned automatically.
            </Typography>
          </Grid>
        )}

        {onDemandScoped ? (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Driver earning model, commission and bidding are transport settings and are not shown here.
              Service provider commission comes from the package or commission rules.
            </Typography>
          </Grid>
        ) : (
        <>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="Partner earning model"
            value={form.partnerPayoutModel}
            onChange={(e) => setForm({ ...form, partnerPayoutModel: e.target.value as ZoneFormState['partnerPayoutModel'] })}
            helperText="How drivers earn in this zone"
          >
            {PAYOUT_MODELS.map((m) => (
              <MenuItem key={m.key} value={m.key}>{m.title}</MenuItem>
            ))}
          </TextField>

        </Grid>

        <Grid item xs={12} sm={4}>

          <NonNegativeNumberField
            fullWidth
            label="Commission rate (%)"
            value={form.commissionRate}
            onValueChange={(n) => setForm({ ...form, commissionRate: n })}
            decimalPlaces={ZONE_COMMISSION_RATE_DECIMALS}
            disabled={!commissionRateUsesZonePercent(form.partnerPayoutModel)}
            error={Boolean(commissionFieldError)}
            helperText={
              commissionFieldError
              || (!commissionRateUsesZonePercent(form.partnerPayoutModel)
                ? 'Not used — subscription zone'
                : `${ZONE_COMMISSION_RATE_MIN}–${ZONE_COMMISSION_RATE_MAX}% · up to ${ZONE_COMMISSION_RATE_DECIMALS} decimals`)
            }
          />

        </Grid>

        <Grid item xs={12}>

          {(form.partnerPayoutModel === 'subscription' || form.partnerPayoutModel === 'both') && (
            <Typography variant="body2" color="text.secondary">
              Partner subscription plans for zone <strong>{form.code || '(set zone code)'}</strong> are managed from{' '}
              <Link component={RouterLink} to="/partners/subscription-plans" underline="hover">
                Partner Subscriptions
              </Link>
              {' '}or the <strong>subscription icon</strong> on the Zones list after saving.
            </Typography>
          )}

        </Grid>
        </>
        )}

      </Grid>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom color={fieldErrors?.services ? 'error' : undefined}>
          {scopeHub === 'delivery'
            ? (lockedServiceSlug === 'food-delivery'
              ? 'Food Delivery service'
              : lockedServiceSlug === 'grocery-delivery'
                ? 'Grocery Delivery service'
                : 'Delivery services in this zone')
            : onDemandScoped
              ? 'Services (on-demand) in this zone'
              : 'Services in this zone (all hubs)'}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          color={fieldErrors?.services ? 'error.main' : 'text.primary'}
          display="block"
          sx={{ mb: 1, opacity: fieldErrors?.services ? 1 : 0.85, lineHeight: 1.45 }}
        >
          {fieldErrors?.services || (scopeHub === 'delivery'
            ? 'Delivery hub only — transport and other hubs are hidden in this flow.'
            : onDemandScoped
              ? 'Services hub only — trip, delivery and other hubs are hidden. Their settings on this zone are kept when you save.'
              : 'Select at least one service before continuing.')}
        </Typography>

        {Object.entries(grouped).map(([hub, services]) => (
          <Box key={hub} sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight={700} color="text.primary">{HUB_LABELS[hub] || hub}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              {services.map((s) => (
                <Chip
                  key={s._id}
                  label={s.name}
                  size="small"
                  color={form.selectedServiceIds.includes(s._id) ? 'primary' : 'default'}
                  variant={form.selectedServiceIds.includes(s._id) ? 'filled' : 'outlined'}
                  onClick={() => toggleService(s)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Box>

      {!hideTransportUi && hasRideHailing && (

        <Box>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom color={showErrors && fieldErrors?.vehicles ? 'error' : undefined}>Transport vehicles (per-vehicle pricing)</Typography>

          <Typography variant="caption" color={showErrors && fieldErrors?.vehicles ? 'error' : 'text.secondary'} display="block" sx={{ mb: 0.5 }}>

            {showErrors && fieldErrors?.vehicles ? fieldErrors.vehicles : 'Select vehicle types available in this zone — each gets its own Ride Now / Ride Later rates.'}

          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={0.5}>

            {vehicleTypes.map((v) => (

              <Chip

                key={v.code}

                label={v.name}

                size="small"

                color={form.selectedVehicleTypeCodes.includes(v.code) ? 'secondary' : 'default'}

                variant={form.selectedVehicleTypeCodes.includes(v.code) ? 'filled' : 'outlined'}

                onClick={() => toggleVehicle(v)}

              />

            ))}

          </Stack>

        </Box>
      )}

      {hasPorter && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom color={showErrors && fieldErrors?.porter ? 'error' : undefined}>Porter masters (zone-dependent)</Typography>
          <Typography variant="caption" color={showErrors && fieldErrors?.porter ? 'error' : 'text.secondary'} display="block" sx={{ mb: 1 }}>
            {showErrors && fieldErrors?.porter
              ? fieldErrors.porter
              : 'Select vehicles, categories, and packages available in this zone — same pattern as transport vehicles.'}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Vehicles</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
            {(porterMasters?.vehicles || []).map((v) => (
              <Chip
                key={v._id}
                label={v.name}
                size="small"
                color={form.porterMasters.vehicleIds.includes(v._id) ? 'secondary' : 'default'}
                variant={form.porterMasters.vehicleIds.includes(v._id) ? 'filled' : 'outlined'}
                onClick={() => togglePorterMaster('vehicleIds', v._id)}
              />
            ))}
            {!porterMasters?.vehicles?.length && (
              <Typography variant="caption" color="text.secondary">No Porter vehicles yet — seed them in Delivery → Porter → Vehicles.</Typography>
            )}
          </Stack>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Categories</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
            {(porterMasters?.categories || []).map((c) => (
              <Chip
                key={c._id}
                label={c.name}
                size="small"
                color={form.porterMasters.categoryIds.includes(c._id) ? 'primary' : 'default'}
                variant={form.porterMasters.categoryIds.includes(c._id) ? 'filled' : 'outlined'}
                onClick={() => togglePorterMaster('categoryIds', c._id)}
              />
            ))}
          </Stack>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Packages</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {(porterMasters?.packages || []).map((p) => (
              <Chip
                key={p._id}
                label={p.name}
                size="small"
                color={form.porterMasters.packageIds.includes(p._id) ? 'primary' : 'default'}
                variant={form.porterMasters.packageIds.includes(p._id) ? 'filled' : 'outlined'}
                onClick={() => togglePorterMaster('packageIds', p._id)}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom color={showErrors && fieldErrors?.polygon ? 'error' : undefined}>
          Zone boundary on map
        </Typography>
        <Typography variant="caption" color={showErrors && fieldErrors?.polygon ? 'error' : 'text.secondary'} display="block" sx={{ mb: 1 }}>
          {showErrors && fieldErrors?.polygon
            ? fieldErrors.polygon
            : 'Draw the service area last. Use Click points on the map.'}
        </Typography>
        <MapErrorBoundary fallbackTitle="Zone map could not load">
          <ZonePolygonMap
            editable
            polygon={form.polygon}
            landPolygons={form.landPolygons}
            error={showErrors ? fieldErrors?.polygon : undefined}
            onPolygonChange={(polygon, landPolygons) => setForm((prev) => ({
              ...prev,
              polygon,
              landPolygons: landPolygons ?? (polygon.length ? prev.landPolygons : []),
            }))}
          />
        </MapErrorBoundary>
      </Box>
    </Stack>
  );
}
